import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { PageLayout } from '@gumtree/shared/src/types/srp';
import { Icon } from '..';
import {
    boxSizes,
    breakpoints,
    colors,
    colorVariables,
    fontSizes,
    fontWeights,
    gutterSizes,
    lineHeights,
    mediaQuery,
} from '../base/theme';

import deliveryTileBg from './images/delivery-tile-bg.png';
import twoColDeliveryTileBg from './images/two-col-delivery-tile.png';

const shouldForwardProp = (p: string) =>
    ![
        'pageLayout',
        'isGridViewOnDesktop',
        'isFeatured',
        'isUrgent',
        'isForSaleCategory',
        'isGridView',
        'isJobsCategory',
        'isServicesCommunityCategory',
    ].includes(p);

export const Wrapper = styled('article', { shouldForwardProp })<{
    pageLayout: PageLayout;
}>`
    position: relative;
    background-color: ${colors.white};
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 0 12px 0 rgb(0 0 0 / 20%);

    ${mediaQuery.until(breakpoints.small)} {
        ${({ pageLayout }) =>
            pageLayout === PageLayout.TwoColumns &&
            `
                box-shadow:none;
                border-radius: 0;
            `};
    }
`;

export const A = styled('a', { shouldForwardProp })<{
    pageLayout: PageLayout;
}>`
    width: 100%;

    ${({ pageLayout }) =>
        pageLayout !== PageLayout.OneColumn && `display: flex; flex-direction: column`};

    ${mediaQuery.from(breakpoints.medium)} {
        display: flex;
    }
`;

export const ImageWrapper = styled('div', { shouldForwardProp })<{
    pageLayout: PageLayout;
}>`
    width: 100%;
    padding-top: 75%;
    position: relative;

    ${({ pageLayout }) => pageLayout !== PageLayout.OneColumn && `flex-shrink: 0;`};

    ${mediaQuery.until(breakpoints.msmall)} {
        ${({ pageLayout }) => pageLayout === PageLayout.TwoColumns && `max-height: 116px;`};
    }

    ${mediaQuery.from(breakpoints.msmall) && mediaQuery.until(breakpoints.small)} {
        ${({ pageLayout }) => pageLayout === PageLayout.TwoColumns && `max-height: 138px;`};
    }

    ${mediaQuery.from(breakpoints.medium)} {
        ${(props) =>
            props.pageLayout === PageLayout.OneColumn &&
            `
                flex-basis: 220px;
                flex-shrink: 0;
                padding-top: 0;
        `};

        ${({ pageLayout }) => pageLayout === PageLayout.TwoColumns && `flex-basis: 186px;`};
    }
`;

export const TopTagWrapper = styled.div`
    width: 100%;
    position: absolute;
    top: 6px;
    left: 6px;
    
    display: flex;
    justify-content: flex-start;
`;

export const ImageBadge = styled('div', { shouldForwardProp })<{
    isUrgent?: boolean;
    isFeatured?: boolean;
    isJobsCategory?: boolean;
    isNewUiForCategory?: boolean;
}>`
    border-radius: 50px;
    color: ${colors.white};
    display: flex;
    align-items: center;
    line-height: 20px;
    font-size: ${fontSizes.small};
    font-weight: ${fontWeights.bold};
    padding: ${gutterSizes.xsmall} ${gutterSizes.base};

    ${({ isUrgent, isNewUiForCategory }) => isUrgent && `background: ${isNewUiForCategory ? '#419F08' : colors.red};`};
    ${({ isFeatured }) => isFeatured && `background: ${colors.blue};`};
    ${({ isJobsCategory }) =>
        !isJobsCategory
            ? `
        margin-right: 6px;
    `
            : `
        margin-bottom: ${gutterSizes.large};
        width: fit-content;
    `};
`;

export const ImageCounterLabelStyle = css`
    background: rgba(0, 0, 0, 0.5);
    color: ${colors.white};
    font-size: ${fontSizes.small};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 ${gutterSizes.base};
    margin-right: ${gutterSizes.small};
    border-radius: 4px;

    ${Icon} {
        padding-right: ${gutterSizes.small};
    }
`;

export const H2 = styled('div', { shouldForwardProp })<{
    isGridView?: boolean;
    isForSaleCategory?: boolean;
}>`
    font-size: ${fontSizes.base};
    font-weight: ${fontWeights.bold};
    line-height: ${lineHeights.base};
    margin-right: 6px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;

    ${({ isGridView }) => isGridView && ` -webkit-line-clamp: 2;`};
    ${({ isGridView, isForSaleCategory }) => isGridView && isForSaleCategory && `height: 44px;`};

    ${mediaQuery.until(breakpoints.medium)} {
        ${({ isForSaleCategory }) => isForSaleCategory && `height: 44px;`};
        -webkit-line-clamp: 2;
    }

    ${mediaQuery.until(breakpoints.small)} {
        ${({ isForSaleCategory }) =>
            isForSaleCategory &&
            `
        font-size: ${fontSizes.small};
        line-height: ${lineHeights.small};
        margin-right: 0;
        `};
    }
`;

export const Description = styled.p`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: ${lineHeights.base};
    min-height: 20px;
    height: 44px;
    margin: 0 0 ${gutterSizes.base};
`;

export const Spotlight = styled.div`
    position: absolute;
    top: 16px;
    background-color: ${colors.green};
    padding: 0 8px;
    color: ${colors.white};
    text-transform: uppercase;
    font-size: ${fontSizes.small};
    line-height: 20px;

    &:before,
    &:after {
        position: absolute;
        content: '';
        border-right: 12px solid transparent;
        right: -12px;
        width: 0;
        height: 0;
    }

    &:before {
        top: 0;
        border-top: 12px solid ${colors.green};
    }

    &:after {
        bottom: 0;
        border-bottom: 12px solid ${colors.green};
    }
`;

export const PriceStyle = css`
    color: ${colorVariables.textPrice};
    font-weight: ${fontWeights.bold};
    font-size: ${fontSizes.header};
    line-height: ${lineHeights.medium};
    min-height: 26px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const VatStyle = css`
    font-weight: ${fontWeights.normal};
    font-size: ${fontSizes.smaller};
`;

export const LocationInline = styled('div', { shouldForwardProp })<{
    isGridView?: boolean;
}>`
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;

    ${({ isGridView }) => isGridView && `-webkit-line-clamp: 1;`};

    ${mediaQuery.until(breakpoints.medium)} {
        -webkit-line-clamp: 1;
    }
`;

export const LocationAtBottomWrapper = styled.div`
    overflow: hidden;
    height: 22px;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: ${gutterSizes.base};

    ${mediaQuery.until(breakpoints.small)} {
        font-size: ${fontSizes.small};
        line-height: ${lineHeights.small};
        margin-top: 0;
    }
`;

export const TagsContainer = styled.div`
    display: flex;
    align-items: center;
`;

export const DeliveryTagWrapper = styled.div`
    height: 24px;
    width: fit-content;
    display: flex;
    gap: ${gutterSizes.small};
    align-items: center;
    justify-content: center;
    border-radius: 20px;
    color: ${colors.fgInfo};
    background-color: ${colors.bgInfo};
    font-size: ${fontSizes.small};
    font-weight: 400;
    line-height: ${lineHeights.base};
    padding: 0 ${gutterSizes.base};
    
    .icon {
        margin-top: 0;
        min-width: 16px;
    }
`;

export const TimeWrapperStyle = css`
    font-size: ${fontSizes.small};
    min-width: fit-content;
    margin-left: ${gutterSizes.large};
    color: ${colors.bark80};
`;

export const ShortTime = styled.div`
    display: block;
    margin-left: ${gutterSizes.small};

    ${mediaQuery.from(breakpoints.small)} {
        display: none;
    }
`;

export const FullTime = styled.div`
    display: block;

    ${mediaQuery.until(breakpoints.small)} {
        display: none;
    }
`;

export const ForSalePrice = styled.div`
    color: ${colorVariables.textPrice};
    font-weight: ${fontWeights.bold};
    font-size: ${fontSizes.header};
    line-height: ${lineHeights.medium};

    ${mediaQuery.until(breakpoints.small)} {
        font-size: ${fontSizes.base};
        line-height: ${lineHeights.base};
    }
`;

export const ForSaleFavorite = styled.div`
    .button {
        padding: 0;
    }

    ${mediaQuery.until(breakpoints.small)} {
        .favourite-icon {
            font-size: ${fontSizes.xlarge}!important;
        }
    }
`;

export const ImageBadgeWrapper = styled.div`
    display: flex;
    flex-direction: row;

    > div {
        margin-right: ${gutterSizes.base};
    }
`;

export const JobsAttributes = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    color: ${colors.darkGrey};
`;

export const JobsLargeLogo = styled.img`
    max-width: 115px;
    max-height: 56px;
    margin-left: ${gutterSizes.large};

    ${mediaQuery.until(breakpoints.small)} {
        display: none;
    }
`;

export const JobsSmallLogo = styled.img`
    max-width: 88px;
    max-height: 44px;
    margin-top: ${gutterSizes.large};

    ${mediaQuery.from(breakpoints.small)} {
        display: none;
    }
`;

export const JobsFooterWrapper = styled.div`
    width: 65%;
`;

export const DeliveryWrapper = styled('article', { shouldForwardProp })<{
    pageLayout: PageLayout;
}>`
    position: relative;
    border-radius: ${boxSizes.borderRadius};
    overflow: hidden;
    padding: 36px 0 36px 32px;
    background: url(${deliveryTileBg}) lightgray 50% / cover no-repeat;
    background-size: cover;

    ${mediaQuery.until(breakpoints.small)} {
        height: 100%;
        box-sizing: border-box;
        padding: 46px 22px;

        svg {
            right: 14px;
            bottom: 12px;
        }

        ${({ pageLayout }) =>
            pageLayout === PageLayout.TwoColumns &&
            `
                background: url(${twoColDeliveryTileBg}) lightgray 50% / cover no-repeat;
                box-shadow:none;
                border-radius: 0;
                padding: 46px 22px 0 22px;
            `};
    }

    svg {
        position: absolute;
        right: 24px;
        bottom: 18px;
    }
`;

export const DeliveryText = styled.p`
    font-size: ${fontSizes.xlarge};
    font-weight: ${fontWeights.bold};
    line-height: 24px;
    color: ${colors.white};
    margin-top: 0;
    margin-bottom: 24px;
`;

// export const TextButton = styled(Button)`
//     height: auto;
//     text-decoration: underline;
//     background: none;
//     border: none;
//     padding: 0px;
//     color: ${theme.palette.primary.main};
//     font-size: ${fontSizes.base};
//     font-weight: ${fontWeights.bold};
//     line-height: ${lineHeights.small};

//     ${mediaQuery.until(breakpoints.medium)} {
//         width: 100%;
//         justify-content: center;
//     }

//     &.selected,
//     &:hover {
//         background: none;
//         color: #54cc0a;
//     }
// `;

export const textButtonCss = (theme) => css`
    && {
        height: auto;
        text-decoration: underline;
        background: none;
        border: none;
        padding: 0px;
        color: ${theme.palette.primary.main};
        font-size: ${fontSizes.base};
        font-weight: ${fontWeights.bold};
        line-height: ${lineHeights.small};

        ${mediaQuery.until(breakpoints.medium)} {
            justify-content: center;
        }

        &.selected,
        &:hover {
            background: none;
            color: ${theme.palette.primary.dark};
        }
    }
`;

export const MonthlyPrice = styled.div`
    display: flex;
    align-items: center;
    gap: ${gutterSizes.small};
`;
